import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { AuthUser } from "@match/shared";
import { ApiError, api, tokens } from "@/lib/api";
import { installInvalidation } from "@/lib/queries";
import { DEFAULT_NAME, setSavedBranding, useShownBranding } from "@/lib/branding";
import { Layout } from "@/components/Layout";
import { Login } from "@/pages/Login";
import { ChangePassword } from "@/pages/ChangePassword";
import { Clients } from "@/pages/Clients";
import { Deleted } from "@/pages/Deleted";
import { ClientDetail } from "@/pages/ClientDetail";
import { ClientLedgerPage } from "@/pages/ClientLedgerPage";
import { Trade } from "@/pages/Trade";
import { SingleTradePage } from "@/pages/SingleTrade";
import { Sessions } from "@/pages/Sessions";
import { Cycles, CycleDetail } from "@/pages/Cycles";
import { SessionDetail } from "@/pages/SessionDetail";
import { PropFirms } from "@/pages/PropFirms";
import { Brokers } from "@/pages/Brokers";
import { ArchivedClients, ArchivedFirmsPage } from "@/pages/Archived";
import { Users } from "@/pages/Users";
import { Compare } from "@/pages/Compare";
import { Notifications } from "@/pages/Notifications";
import { Branding } from "@/pages/Branding";
import { Events } from "@/pages/Events";
import { Audit } from "@/pages/Audit";
import { EconomicCalendar } from "@/pages/EconomicCalendar";
import { Notes } from "@/pages/Notes";
import { Platform } from "@/pages/Platform";
import { MyHome, MyLedger } from "@/pages/Me";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      // 4xx non si ritenta: un 403 o un 404 non guariscono da soli.
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});
// Ogni scrittura riuscita ricarica le risorse che tocca (lib/queries.ts).
installInvalidation(queryClient);

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  // Il marchio salvato, o la bozza della pagina Branding (lib/branding.ts).
  const branding = useShownBranding();
  const [loading, setLoading] = useState(true);

  /*
   * IL MARCHIO ARRIVA COL TOKEN, e prima dell'accesso non arriva affatto.
   *
   * Da quando il tenant si riconosce dal token e non dall'indirizzo, la
   * schermata di login e' la stessa per tutti: neutra, «Match Trading». Non e'
   * una rinuncia — un logo prima di sapere chi sta entrando non serve a
   * nessuno, e con un indirizzo solo non c'e' niente da cui dedurlo. Su un
   * dominio custom il backend lo risolve lo stesso, quindi li' si vede anche
   * prima. Colori, icona e titolo li applica `setSavedBranding`.
   */
  const caricaMarchio = async () => {
    setSavedBranding(await api.branding().catch(() => null));
  };

  useEffect(() => {
    void (async () => {
      await caricaMarchio();

      if (tokens.access) {
        const me = await api.me().catch(() => null);
        if (me) setUser(me);
        else tokens.clear();
      }
      setLoading(false);
    })();
  }, []);

  const tenantName = branding?.name ?? DEFAULT_NAME;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page text-sm text-muted">
        Caricamento…
      </div>
    );
  }

  if (!user) {
    return (
      <Login
        onSignedIn={(u) => {
          setUser(u);
          // Adesso il tenant si sa: il marchio si chiede col token.
          void caricaMarchio();
        }}
        tenantName={tenantName}
        logoUrl={branding?.logoDarkUrl ?? null}
      />
    );
  }

  /*
   * IL CAMBIO OBBLIGATO PRENDE IL POSTO DI TUTTO IL RESTO, come `Login`.
   *
   * Non un banner e non una voce di menu: finche' quella password e' quella che
   * gli abbiamo comunicato noi, **nessuna pagina del gestionale e' montata**.
   * Il backend rifiuta comunque (`lib/must-change-password.ts`): qui si evita di
   * mostrare porte chiuse, la' si tengono chiuse.
   */
  if (user.mustChangePassword) {
    return <ChangePassword user={user} tenantName={tenantName} onChanged={setUser} />;
  }

  const signOut = () => {
    // I dati in cache appartengono all'utente che esce. Il marchio anche.
    queryClient.clear();
    setUser(null);
    setSavedBranding(null);
    // Il token non c'e' piu' (api.logout): torna il marchio dell'indirizzo, che
    // su un dominio custom e' quello del tenant e sull'indirizzo comune nessuno.
    void caricaMarchio();
  };

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout user={user} tenantName={tenantName} logoUrl={branding?.logoDarkUrl} onSignedOut={signOut} />}>
            {user.role === "CLIENT" ? (
              /*
               * L'AREA CLIENTE E' UNA TABELLA DI ROTTE SUA, non quella
               * dell'operatore con delle guardie sopra.
               *
               * E' la stessa forma dell'allowlist del backend, e per la stessa
               * ragione: **una rotta nuova nasce chiusa**. Montare le pagine
               * dell'operatore e sperare che ciascuna risponda 403 vuol dire
               * affidarsi a un controllo per pagina, cioe' alla cosa che
               * `lib/client-scope.ts` ha smesso di fare apposta. Qui il cliente
               * non puo' arrivare a una pagina nuova perche' non c'e', non
               * perche' qualcuno si e' ricordato di filtrarla.
               *
               * Il `*` in fondo riporta ai suoi conti: un indirizzo scritto a
               * mano non deve dare una pagina bianca.
               */
              <>
                <Route index element={<MyHome />} />
                <Route path="cicli" element={<Cycles />} />
                <Route path="cicli/:id" element={<CycleDetail />} />
                <Route path="movimenti" element={<MyLedger />} />
                <Route path="confronto" element={<Compare />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              <>
            {/*
             * La pagina d'arrivo e' Note e calendario: «/» porta a «/note»,
             * cosi' l'indirizzo e la voce accesa del menu sono gli stessi da
             * qualunque parte si entri. Tutti i ruoli interni leggono le note
             * (ADMIN, OPERATORE, CONTABILE, VIEWER); il CLIENT ha la sua tabella
             * sopra e resta sui suoi conti.
             */}
            <Route index element={<Navigate to="/note" replace />} />
            <Route path="clienti" element={<Clients />} />
            <Route path="clienti/archiviati" element={<ArchivedClients />} />
            <Route path="clienti/:id" element={<ClientDetail />} />
            <Route path="clienti/:id/movimenti" element={<ClientLedgerPage />} />
            {/* L'operazione singola su un conto: solo interni, come tutta questa tabella di rotte. */}
            <Route path="conti/:id/operazione" element={<SingleTradePage />} />
            <Route path="prop-firm" element={<PropFirms />} />
            <Route path="prop-firm/archiviati" element={<ArchivedFirmsPage kind="PROP" />} />
            <Route path="broker" element={<Brokers />} />
            <Route path="broker/archiviati" element={<ArchivedFirmsPage kind="BROKER" />} />
            <Route path="eliminati" element={<Deleted />} />
            <Route path="operazione" element={<Trade />} />
            <Route path="cicli" element={<Cycles />} />
            <Route path="cicli/:id" element={<CycleDetail />} />
            <Route path="fasi" element={<Sessions />} />
            <Route path="fasi/:id" element={<SessionDetail />} />
            <Route path="confronto" element={<Compare />} />
            <Route path="eventi" element={<Events />} />
            {/* Solo interni, come tutta questa tabella: al cliente la pagina non esiste. */}
            <Route path="calendario-economico" element={<EconomicCalendar />} />
            <Route path="note" element={<Notes />} />
            <Route path="audit" element={<Audit />} />
            <Route path="utenti" element={<Users />} />
            <Route path="impostazioni/notifiche" element={<Notifications />} />
            <Route path="impostazioni/branding" element={<Branding />} />
            {/* Solo il super admin di piattaforma: per gli altri la rotta non esiste, e il backend rifiuta comunque. */}
            {user.isPlatformAdmin && <Route path="platform" element={<Platform />} />}
              </>
            )}
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
